<template>
  <full-card v-show="showCard">
    <template v-slot:header>
      Auslastung
    </template>

    <template v-slot:action>
      <button-primary
          @click="bookDesk"
          v-show="show_action && whichDashboard==='old'"
          style="background-color: rgb(248, 178, 65)!important"
      >
        <template v-slot:icon>
          <i class="fa fa-calendar-check-o"></i>
        </template>
        Schreibtisch buchen
      </button-primary>
    </template>

    <schedule-create v-show="false" ref="create" @created="reload"></schedule-create>

    <div v-if="floor && !desk">
      <select-menu
          v-model.number="floor"
          :text="floors.map(f => f.name)"
          :values="floors.map(f => f.id)"
      >
        Etage
      </select-menu>

      <div class="mb-4">
        <navigation-floor
            :items="availableItems"
            :unavailable="unavailableItems"
            :hybrid="true"
            @selected="bookDesk"
        ></navigation-floor>
      </div>

      <p v-show="availableItems.length === 0" class="font-bold">
        Keine buchbaren {{ whichDashboard === 'new' ? 'Ressourcen' : 'Schreibtische' }} gefunden.
      </p>
    </div>
  </full-card>
</template>

<script>
import Card from "./UI/Card";
import Loading from "./UI/Loading";
import SelectMenu from "./UI/SelectMenu";
import TailwindList from "./UI/TailwindList";
import orderBy from "lodash/orderBy";
import uniqBy from "lodash/uniqBy";
import NavigationFloor from "./NavigationFloor";
import FullCard from "./UI/FullCard";
import DeskCreate from "./DeskCreate.vue";
import Reservation from "./Reservation.vue";
import ScheduleCreate from "./ScheduleCreate.vue";
import ButtonPrimary from "./UI/ButtonPrimary.vue";

export default {
  name: "Utilization",
  components: {
    ButtonPrimary,
    ScheduleCreate,
    Reservation,
    DeskCreate,
    FullCard,
    NavigationFloor,
    TailwindList,
    SelectMenu,
    Loading,
    Card,
  },

  inject: ["group", "can"],

  props: {
    show_action: {
      type: Boolean,
      default: false,
    },
    just_map: {
      type: Boolean,
      default: false,
    },
  },

  data: () => ({
    loading: true,
    desk: null,
    floor: null,
    showCard: false,
    suggestions: [],
    unavailable: [],
    availableConferenceRooms: [],
    unavailableConferenceRooms: [],
    availableBookables: [],      // <- added
    unavailableBookables: [],    // <- added
  }),

  mounted() {
    this.$root.$on('refresh', () => {
      this.reload()
    });
  },

  created() {
    this.init();
  },

  computed: {
    whichDashboard() {
      const path = window.location.pathname;
      const parts = path.split('/').filter(p => p.length > 0);

      if (parts[0] === 'admin') {
        return 'old';
      } else if (parts[0] === 'admin-panel') {
        return 'new';
      }

      return parts.length > 0 ? parts[0] : null;
    },


    availableItems() {
      if (!this.floor) {
        return [];
      }

      const sources = [
        {
          items: this.suggestions || [],
          type: 'desk',
          filterFn: (desk) => desk.floor?.id === this.floor,
        },
      ];

      if (this.whichDashboard === 'new') {
        sources.push(
            {
              items: this.availableConferenceRooms || [],
              type: 'conference_room',
              filterFn: (item) => item.floor?.id === this.floor,
            },
            {
              items: this.availableBookables || [],
              type: "bookable",
              filterFn: (item) => item.floor?.id === this.floor,
            }
        );
      }

      const mergedItems = sources.reduce((acc, { items, type, filterFn }) => {
        const filtered = (items || [])
            .filter(filterFn)
            .map((item) => ({ ...item, floor: item.floor, item_type: type }));
        return acc.concat(filtered);
      }, []);

      return orderBy(mergedItems, ['name'], ['asc']);
    },

    floors() {
      // merge all sources into one array
      const merged = [
        ...this.suggestions,
        ...this.unavailable,
        ...this.availableBookables,
        ...this.availableConferenceRooms,
        ...this.unavailableBookables,
        ...this.unavailableConferenceRooms
      ];

      return uniqBy(
          merged
              .filter((d) => d.floor?.map_link) // keep only with floor + map_link
              .map((d) => d.floor) // extract the floor object
              .sort((a, b) => a.name.localeCompare(b.name)),
          "id"
      );
    },

    unavailableItems() {
      if (!this.floor) return [];

      const sources = [
        {
          items: this.unavailable || [],
          type: "desk",
          filterFn: (desk) => desk.floor?.id === this.floor,
        },
      ];
      if (this.whichDashboard === "new") {
        sources.push(
            {
              items: this.unavailableConferenceRooms || [],
              type: "conference_room",
              filterFn: (item) => item.floor?.id === this.floor,
            },
            {
              items: this.unavailableBookables || [],
              type: "bookable",
              filterFn: (item) => item.floor?.id === this.floor,
            }
        );
      }

      return sources.reduce((acc, { items, type, filterFn }) => {
        const filtered = (items || [])
            .filter(filterFn)
            .map((item) => ({ ...item, floor: item.floor, item_type: type }));
        return acc.concat(filtered);
      }, []);
    },
  },

  methods: {
    init() {
      this.loading = true;
      this.suggestions = [];
      this.unavailable = [];
      this.availableConferenceRooms = [];
      this.unavailableConferenceRooms = [];
      this.availableBookables = [];
      this.unavailableBookables = [];
      this.desk = null;

      const date = new Date().toISOString().substring(0, 10);

      if (this.can.create.schedule) {
        this.$http
            .get(`/admin/reservations?from=${date}&to=${date}`)
            .then((response) => {
              this.suggestions = response.data.data || [];
              this.unavailable = response.data.unavailable || [];
              this.availableConferenceRooms =
                  response.data.availableConferenceRooms || [];
              this.unavailableConferenceRooms =
                  response.data.unavailableConferenceRooms || [];
              this.availableBookables = response.data.availableBookables || [];
              this.unavailableBookables = response.data.unavailableBookables || [];

              // Wait a tick so computed `floors` updates, then set/repair selected floor
              this.$nextTick(() => {
                const first = this.floors.length ? this.floors[0].id : null;
                if (!this.floor || !this.floors.some((f) => f.id === this.floor)) {
                  this.floor = first;
                }
                this.showCard = !!this.floor;
                this.loading = false;
              });
            })
            .catch((error) => {
              this.loading = false;
              this.$root.$emit("error", error);
            });
      }
    },

    bookDesk(item, type = "desk") {
      if (type === "desk") {
        this.$refs.create.bookDesk(item);
      }
      /* else if (type === 'conference_room') {
           window.location.href = `/admin/pages/conference_rooms?roomId=${item.id}`;
         } */
    },

    reload() {
      this.$emit("reload");
      this.init();
    },
  },
};
</script>

<style scoped>
</style>
